import openSocket from "socket.io-client";
import api from "./api";
import { getBackendUrl } from "../config";

let refreshSocketTokenPromise = null;
let sharedSocket = null;
let activeSocketConsumers = 0;
let idleDisconnectTimer = null;
const SOCKET_IDLE_DISCONNECT_MS = 24 * 60 * 60 * 1000;

const getSocketToken = () => {
  const rawToken = localStorage.getItem("token");
  if (!rawToken) return "";

  try {
    const parsedToken = JSON.parse(rawToken);
    return typeof parsedToken === "string" ? parsedToken : "";
  } catch (error) {
    return typeof rawToken === "string" ? rawToken : "";
  }
};

const decodeJwtPayload = token => {
  if (!token || typeof token !== "string") {
    return null;
  }

  try {
    const payloadSegment = token.split(".")[1];
    if (!payloadSegment) {
      return null;
    }

    const base64 = payloadSegment.replace(/-/g, "+").replace(/_/g, "/");
    const normalizedBase64 = base64.padEnd(
      base64.length + ((4 - (base64.length % 4)) % 4),
      "="
    );

    return JSON.parse(window.atob(normalizedBase64));
  } catch (error) {
    return null;
  }
};

const isUsableSocketToken = token => {
  const payload = decodeJwtPayload(token);
  const expirationTime = Number(payload?.exp);

  if (!Number.isFinite(expirationTime) || expirationTime <= 0) {
    return false;
  }

  const expirationMs = expirationTime * 1000;
  const clockSkewMs = 5000;
  return Date.now() < expirationMs - clockSkewMs;
};

const applySocketToken = (socket, token) => {
  const normalizedToken = token || "";
  socket.io.opts.query = { token: normalizedToken };
  socket.auth = { token: normalizedToken };
};

const saveSocketToken = token => {
  if (!token) {
    return;
  }

  localStorage.setItem("token", JSON.stringify(token));
  api.defaults.headers.Authorization = `Bearer ${token}`;
};

const isSessionExpiredError = error =>
  error?.response?.status === 401 &&
  error?.response?.data?.error === "ERR_SESSION_EXPIRED";

const refreshSocketToken = async () => {
  if (!refreshSocketTokenPromise) {
    refreshSocketTokenPromise = api
      .post("/auth/refresh_token")
      .then(({ data }) => {
        if (!data?.token) {
          throw new Error("ERR_REFRESH_TOKEN_MISSING");
        }

        saveSocketToken(data.token);
        return data.token;
      })
      .finally(() => {
        refreshSocketTokenPromise = null;
      });
  }

  return refreshSocketTokenPromise;
};

const clearIdleDisconnectTimer = () => {
  if (!idleDisconnectTimer) {
    return;
  }

  clearTimeout(idleDisconnectTimer);
  idleDisconnectTimer = null;
};

const scheduleIdleDisconnect = () => {
  clearIdleDisconnectTimer();

  idleDisconnectTimer = setTimeout(() => {
    if (activeSocketConsumers === 0 && sharedSocket?.connected) {
      sharedSocket.disconnect();
    }
  }, SOCKET_IDLE_DISCONNECT_MS);
};

const createSocketConnection = () => {
  const token = getSocketToken();
  const isTunnelHost =
    /ngrok(-free)?\.app$/i.test(window.location.hostname) ||
    /trycloudflare\.com$/i.test(window.location.hostname);
  const transports = isTunnelHost
    ? ["polling"]
    : ["polling", "websocket"];

  const socket = openSocket(getBackendUrl(), {
    transports,
    query: { token },
    auth: { token },
    reconnection: true,
    reconnectionAttempts: Infinity,
    reconnectionDelay: 1000,
    reconnectionDelayMax: 5000,
    randomizationFactor: 0.3,
    timeout: 20000,
    upgrade: !isTunnelHost,
    autoConnect: false
  });

  let isRecoveringAuth = false;
  let reconnectFallbackTimer = null;
  let forcedPollingMode = false;

  const scheduleReconnectFallback = (delayMs = 1500) => {
    if (reconnectFallbackTimer) {
      return;
    }

    reconnectFallbackTimer = setTimeout(() => {
      reconnectFallbackTimer = null;
      if (!socket.connected) {
        socket.connect();
      }
    }, delayMs);
  };

  const recoverSocketAuth = async () => {
    if (isRecoveringAuth) {
      return;
    }

    isRecoveringAuth = true;

    try {
      const renewedToken = await refreshSocketToken();
      applySocketToken(socket, renewedToken);

      if (!socket.connected) {
        socket.connect();
      }
    } catch (error) {
      const fallbackToken = getSocketToken();
      const hasUsableFallbackToken = isUsableSocketToken(fallbackToken);

      if (hasUsableFallbackToken) {
        applySocketToken(socket, fallbackToken);

        if (!socket.connected) {
          socket.connect();
        }
        return;
      }

      if (isSessionExpiredError(error)) {
        // Session is truly expired, keep socket disconnected.
        socket.disconnect();
      } else {
        // Transient network/auth refresh issue: retry with current token.
        if (fallbackToken) {
          applySocketToken(socket, fallbackToken);
        }

        scheduleReconnectFallback();
      }
    } finally {
      isRecoveringAuth = false;
    }
  };

  socket.io.on("reconnect_attempt", async () => {
    const freshToken = getSocketToken();

    if (isUsableSocketToken(freshToken)) {
      applySocketToken(socket, freshToken);
      return;
    }

    await recoverSocketAuth();
  });

  socket.on("connect", () => {
    forcedPollingMode = false;
    if (reconnectFallbackTimer) {
      clearTimeout(reconnectFallbackTimer);
      reconnectFallbackTimer = null;
    }
  });

  socket.on("connect_error", async error => {
    const message = `${error?.message || ""}`.toUpperCase();
    const isTransportError =
      message.includes("WEBSOCKET") ||
      message.includes("TRANSPORT") ||
      message.includes("XHR") ||
      message.includes("NETWORK");
    const shouldRecoverAuth =
      message.includes("AUTH_TOKEN") ||
      message.includes("TOKEN") ||
      message.includes("JWT");
    const freshToken = getSocketToken();
    const hasUsableToken = isUsableSocketToken(freshToken);

    if (shouldRecoverAuth || !hasUsableToken) {
      await recoverSocketAuth();
      return;
    }

    if (isTransportError && !forcedPollingMode) {
      forcedPollingMode = true;
      socket.io.opts.transports = ["polling"];
      socket.io.opts.upgrade = false;
      scheduleReconnectFallback(500);
      return;
    }

    if (isTransportError) {
      scheduleReconnectFallback(1200);
    }
  });

  socket.on("disconnect", async reason => {
    if (reason === "io server disconnect") {
      await recoverSocketAuth();
      return;
    }

    const fallbackToken = getSocketToken();
    if (fallbackToken) {
      applySocketToken(socket, fallbackToken);
    }

    scheduleReconnectFallback(1200);
  });

  const connect = async () => {
    const initialToken = getSocketToken();

    if (isUsableSocketToken(initialToken)) {
      applySocketToken(socket, initialToken);
      socket.connect();
      return;
    }

    try {
      const renewedToken = await refreshSocketToken();
      applySocketToken(socket, renewedToken);
      socket.connect();
    } catch (error) {
      // Session probably expired; socket remains disconnected.
    }
  };

  const ensureSocketConnected = async () => {
    if (socket.connected) {
      return;
    }

    const freshToken = getSocketToken();
    if (isUsableSocketToken(freshToken)) {
      applySocketToken(socket, freshToken);
      socket.connect();
      return;
    }

    await recoverSocketAuth();
  };

  window.addEventListener("online", () => {
    ensureSocketConnected().catch(() => undefined);
  });

  window.addEventListener("focus", () => {
    ensureSocketConnected().catch(() => undefined);
  });

  document.addEventListener("visibilitychange", () => {
    if (!document.hidden) {
      ensureSocketConnected().catch(() => undefined);
    }
  });

  // Defer initial connect to the next tick so callers can register listeners first.
  setTimeout(() => {
    connect();
  }, 0);

  return socket;
};

function connectToSocket() {
  if (!sharedSocket) {
    sharedSocket = createSocketConnection();
  }

  activeSocketConsumers += 1;
  clearIdleDisconnectTimer();

  const listeners = [];
  let released = false;
  let handle;

  const release = () => {
    if (released) {
      return;
    }

    released = true;
    listeners.forEach(({ event, listener }) => {
      sharedSocket.off(event, listener);
    });

    activeSocketConsumers = Math.max(0, activeSocketConsumers - 1);

    if (activeSocketConsumers === 0) {
      scheduleIdleDisconnect();
    }
  };

  if (!sharedSocket.connected) {
    const freshToken = getSocketToken();
    if (isUsableSocketToken(freshToken)) {
      applySocketToken(sharedSocket, freshToken);
    }
    sharedSocket.connect();
  }

  handle = new Proxy(sharedSocket, {
    get(target, prop) {
      if (prop === "disconnect" || prop === "close") {
        return release;
      }

      if (prop === "on" || prop === "once") {
        return (event, listener) => {
          if (typeof listener === "function") {
            listeners.push({ event, listener });
          }

          target[prop](event, listener);
          return handle;
        };
      }

      if (prop === "off") {
        return (event, listener) => {
          target.off(event, listener);
          if (typeof listener === "function") {
            const index = listeners.findIndex(
              item => item.event === event && item.listener === listener
            );
            if (index >= 0) {
              listeners.splice(index, 1);
            }
          }

          return handle;
        };
      }

      const value = target[prop];
      return typeof value === "function" ? value.bind(target) : value;
    }
  });

  return handle;
}

export default connectToSocket;
